import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTargetRect } from "./useTargetRect";
import type { TourDefinition, TourStep } from "./types";

interface Props {
  tour: TourDefinition;
  step: TourStep;
  stepIndex: number;
  stepCount: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
}

const CARD_WIDTH = 320;
const GAP = 14; // space between spotlight and card
const PAD = 8; // spotlight halo + viewport clamp padding

function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.closest !== "function") return false;
  // `contenteditable` is editable when bare (`<div contenteditable>`) or set to
  // the empty string, not only when it is literally "true" — so match on the
  // attribute's presence and exclude the one value that turns it off.
  return !!el.closest(
    "input, textarea, select, [contenteditable]:not([contenteditable='false'])",
  );
}

export function GuideSpotlight({
  tour,
  step,
  stepIndex,
  stepCount,
  onNext,
  onPrev,
  onSkip,
}: Props) {
  const { t, direction } = useLanguage();
  // `null` when the step is anchorless OR its anchor is missing/hidden — both
  // render a centred card over a fully dimmed screen (the mobile fallback).
  const rect = useTargetRect(step.target);
  const anchorHidden = !!step.target && !rect;
  const rootRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardPos, setCardPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const isFirst = stepIndex === 0;
  const isLast = stepIndex === stepCount - 1;

  // Esc skips the whole tour; arrow keys advance/retreat, mirrored for RTL.
  //
  // Listen on `window` in the CAPTURE phase: a Radix modal layer (Dialog,
  // AlertDialog, Sheet, Drawer…) listens for Escape on `document` in capture,
  // and the tour renders ABOVE it, so the tour must consume Escape first —
  // otherwise Escape dismisses the layer underneath instead of the tour that
  // the user is actually looking at. Window-capture always precedes
  // document-capture, so this wins regardless of mount order.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onSkip();
        return;
      }
      // Never steal arrows from a field the user is typing in — the task modal
      // behind the tour is full of them, and caret movement must not flip steps.
      if (isEditableTarget(e.target)) return;
      if (e.key === "ArrowRight") {
        if (direction === "rtl") onPrev();
        else onNext();
      }
      if (e.key === "ArrowLeft") {
        if (direction === "rtl") onNext();
        else onPrev();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onSkip, onNext, onPrev, direction]);

  // A Radix modal layer detects "clicked outside me" via a `pointerdown`
  // listener on `document`. The tour sits outside every such layer, so without
  // this a click on Next/Back/Skip would also dismiss the dialog underneath.
  // Swallow pointerdown that originates inside the tour before it reaches
  // document. `click` is a separate event, so the card's own onClick handlers
  // still fire.
  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current?.contains(e.target as Node)) e.stopPropagation();
    };
    window.addEventListener("pointerdown", onPointerDown, true);
    return () => window.removeEventListener("pointerdown", onPointerDown, true);
  }, []);

  // Position the card relative to the spotlight (or center it when anchorless),
  // measuring the card so we can flip/clamp without overflow.
  useLayoutEffect(() => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const card = cardRef.current;
    const cardH = card?.offsetHeight ?? 180;
    const cardW = card?.offsetWidth ?? CARD_WIDTH;

    if (!rect || step.placement === "center") {
      setCardPos({ top: Math.max(PAD, (vh - cardH) / 2), left: Math.max(PAD, (vw - cardW) / 2) });
      return;
    }

    const centerX = rect.left + rect.width / 2;
    // Physical edges. "end" means the inline-end side of the anchor: its right
    // in LTR, its left in RTL ("start" is the mirror). The RTL branch used to
    // swap these too, which double-mirrored and dropped the card ON the anchor.
    const rightEdge = rect.left + rect.width;
    const leftEdge = rect.left;

    let top: number;
    let left: number;
    const place = step.placement ?? "bottom";

    const below = rect.top + rect.height + GAP;
    const above = rect.top - GAP - cardH;
    const wantTop = place === "top";
    // Vertical: honor preference, else flip to whichever side has room.
    if (place === "start" || place === "end") {
      top = rect.top + rect.height / 2 - cardH / 2;
      const toTheRight = (place === "end") === (direction !== "rtl");
      left = toTheRight ? rightEdge + GAP : leftEdge - GAP - cardW;
    } else {
      top = wantTop && above >= PAD ? above : below + cardH <= vh - PAD ? below : Math.max(PAD, above);
      left = centerX - cardW / 2;
    }

    // Clamp inside the viewport.
    top = Math.min(Math.max(PAD, top), vh - cardH - PAD);
    left = Math.min(Math.max(PAD, left), vw - cardW - PAD);
    setCardPos({ top, left });
  }, [rect, step.placement, step.target, direction, stepIndex]);

  const spotlight = rect
    ? {
        top: rect.top - PAD,
        left: rect.left - PAD,
        width: rect.width + PAD * 2,
        height: rect.height + PAD * 2,
      }
    : null;

  return createPortal(
    <div
      ref={rootRef}
      className="fixed inset-0 z-[120]"
      role="dialog"
      aria-modal="true"
      aria-label={t(tour.titleAr, tour.titleEn)}
      // While a Radix modal layer is open it sets `pointer-events: none` on
      // <body> and re-enables it only on its own content. The tour is a sibling
      // portal on <body>, so it inherits that `none` and every control on the
      // card goes dead — visible on top, unclickable. (Repro: a deep-linked
      // /dashboard?tab=tasks&task=<id> opens the task modal while the pipeline
      // tour auto-starts; the tour could not be dismissed.) Re-assert `auto`
      // here so the tour is interactive over ANY overlay, not just that one.
      style={{ pointerEvents: "auto" }}
    >
      {/* Click-blocker — interaction is paused during the tour. */}
      <div className="absolute inset-0" onClick={onSkip} />

      {/* Spotlight cutout (visual only). The huge box-shadow dims everything else. */}
      {spotlight && (
        <div
          className="pointer-events-none absolute rounded-lg ring-2 ring-primary/70 transition-all duration-200"
          style={{
            top: spotlight.top,
            left: spotlight.left,
            width: spotlight.width,
            height: spotlight.height,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
          }}
        />
      )}
      {/* No anchor → dim the whole screen. */}
      {!spotlight && <div className="absolute inset-0 bg-black/55" />}

      {/* Tooltip card */}
      <div
        ref={cardRef}
        className="absolute w-80 max-w-[calc(100vw-1rem)] rounded-xl border bg-card p-4 text-card-foreground shadow-2xl"
        style={{ top: cardPos.top, left: cardPos.left }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-start justify-between gap-2">
          <p className="text-xs font-medium text-muted-foreground">
            {t(tour.titleAr, tour.titleEn)} · {stepIndex + 1}/{stepCount}
          </p>
          <button
            type="button"
            onClick={onSkip}
            className="-me-1 -mt-1 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label={t("إغلاق", "Close")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <h3 className="mb-1 text-base font-semibold">{t(step.titleAr, step.titleEn)}</h3>
        <p className="mb-4 text-sm leading-relaxed text-muted-foreground">
          {t(step.bodyAr, step.bodyEn)}
        </p>
        {/* Anchor not visible (e.g. sidebar folded into the mobile menu): the
            card is centred, so say where the item lives instead of pointing. */}
        {anchorHidden && step.fallbackHintAr && step.fallbackHintEn && (
          <p className="-mt-2 mb-4 text-sm font-medium" data-testid="guide-fallback-hint">
            {t(step.fallbackHintAr, step.fallbackHintEn)}
          </p>
        )}

        {/* Progress dots */}
        <div className="mb-3 flex items-center gap-1.5">
          {Array.from({ length: stepCount }).map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === stepIndex ? "w-5 bg-primary" : "w-1.5 bg-muted-foreground/30"
              }`}
            />
          ))}
        </div>

        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" size="sm" onClick={onSkip}>
            {t("تخطّي", "Skip")}
          </Button>
          <div className="flex items-center gap-2">
            {!isFirst && (
              <Button variant="outline" size="sm" onClick={onPrev}>
                {t("السابق", "Back")}
              </Button>
            )}
            <Button size="sm" onClick={onNext}>
              {isLast ? t("تم", "Done") : t("التالي", "Next")}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
